/**
 * HR Suite — the ONE HTTP layer every HR hook goes through.
 *
 * Server envelope is `{ status: "success", data }` for every HR endpoint
 * (see types/hr.contract.ts) — these helpers unwrap `data` so hooks deal in
 * contract types only. Errors keep axios's shape; read them with
 * `hrErrorMessage` / `hrErrorData` (a lost race is 409 with
 * `data.currentStatus`; HR switched off is 403 with `data.code === "HR_DISABLED"`).
 *
 * Query keys all live under ["hr", …] so one `invalidateQueries({ queryKey:
 * HR_KEYS.all })` refreshes every HR screen after a cross-cutting write
 * (e.g. approving leave changes the dashboard, register and leave queue).
 */

import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import type {
  HrLevel,
  HrMeResponse,
  HrModuleKey,
  SuitesResponse,
} from "../types/hr.contract";

export const HR_API = "/suites/hr";
export const SUITES_API = "/suites";

export const HR_KEYS = {
  all: ["hr"] as const,
  me: ["hr", "me"] as const,
  /** GET /approvals — every pending request the caller may decide. */
  approvals: ["hr", "approvals"] as const,
  suites: ["suites"] as const,
};

type Params = Record<string, string | number | boolean | null | undefined>;

function clean(params?: Params) {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
}

export async function hrGet<T>(path: string, params?: Params): Promise<T> {
  const res = await axiosInstance.get(`${HR_API}${path}`, { params: clean(params) });
  return res.data?.data as T;
}

export async function hrPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await axiosInstance.post(`${HR_API}${path}`, body ?? {});
  return res.data?.data as T;
}

export async function hrPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await axiosInstance.patch(`${HR_API}${path}`, body ?? {});
  return res.data?.data as T;
}

export async function hrPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await axiosInstance.put(`${HR_API}${path}`, body ?? {});
  return res.data?.data as T;
}

export async function hrDelete<T>(path: string, params?: Params): Promise<T> {
  const res = await axiosInstance.delete(`${HR_API}${path}`, { params: clean(params) });
  return res.data?.data as T;
}

/**
 * GET a CSV endpoint and hand it to the browser as a download. Server-side
 * export re-runs the scoped query, so the file is the WHOLE result — never
 * just the page that happens to be loaded.
 */
export async function hrDownload(path: string, params: Params | undefined, fallbackName: string): Promise<void> {
  const res = await axiosInstance.get(`${HR_API}${path}`, {
    params: clean(params),
    responseType: "blob",
  });
  const disposition: string = res.headers?.["content-disposition"] ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const name = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** The slice of an axios error the HR screens read. */
interface HrHttpError {
  message?: string;
  response?: { status?: number; data?: { message?: string; data?: unknown } };
}

export function hrErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  const e = err as HrHttpError | undefined;
  return e?.response?.data?.message || e?.message || fallback;
}

export function hrErrorStatus(err: unknown): number | undefined {
  return (err as HrHttpError | undefined)?.response?.status;
}

export function hrErrorData<T = unknown>(err: unknown): T | undefined {
  return (err as HrHttpError | undefined)?.response?.data?.data as T | undefined;
}

/* ───────────────────────────────────────────── suite + session bootstrap */

/** GET /suites — is HR switched on for this tenant (and may I toggle it)? */
export function useSuites() {
  return useQuery({
    queryKey: HR_KEYS.suites,
    queryFn: async () => {
      const res = await axiosInstance.get(SUITES_API);
      return (res.data?.data ?? { suites: [] }) as SuitesResponse;
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

export function useHrEnabled(): { enabled: boolean; isLoading: boolean } {
  const { data, isLoading } = useSuites();
  return { enabled: !!data?.suites?.find((s) => s.key === "hr")?.isEnabled, isLoading };
}

/** GET /suites/hr/me — only fetched once HR is known to be enabled. */
export function useHrMe() {
  const { enabled } = useHrEnabled();
  return useQuery({
    queryKey: HR_KEYS.me,
    queryFn: () => hrGet<HrMeResponse>("/me"),
    enabled,
    staleTime: 60_000,
    retry: 0,
  });
}

/**
 * Capability checks for UI gating. `level()` mirrors the server resolver
 * (all | own | none); `can()` is "not none". The server enforces the same
 * decisions on every route — this only hides doors that would 403.
 */
export function useHrAccess() {
  const { enabled, isLoading: suitesLoading } = useHrEnabled();
  const me = useHrMe();
  const level = (module: HrModuleKey, action: string): HrLevel =>
    (me.data?.access?.[module]?.[action] as HrLevel | undefined) ?? "none";
  return {
    enabled,
    isLoading: suitesLoading || (enabled && me.isLoading),
    me: me.data ?? null,
    isAdmin: !!me.data?.isAdmin,
    level,
    can: (module: HrModuleKey, action: string) => level(module, action) !== "none",
    canAll: (module: HrModuleKey, action: string) => level(module, action) === "all",
  };
}
